/**
 * @file    useCurrentAlias.ts
 * @scope   SHARED
 * @summary Owns the currently selected chart option and the derived Enterprise discriminator,
 *          keeping both in sync with the global store.
 * @consumes `customWidget.customWidgetConfig`, global store setter, an injected Enterprise
 *           chart-type resolver
 * @produces currentAlias, setCurrentAlias, enterpriseChartType, isDiyWidgetEngine,
 *           hasEnterpriseV2Config, chartTypes
 * @sideEffects Writes `isDiyWidgetEngine` to the global store on every alias change.
 * @notes This is the single source of truth for the legacy/enterprise split. The resolver is
 *        INJECTED (never imported) because `common/` must not depend on `Enterprise/`; the
 *        drawer passes the registry seam in. `isDiyWidgetEngine` is now a derived convenience
 *        (`enterpriseChartType != null`) so upcoming Enterprise chart types only have to be
 *        registered, not branched on here.
 */
import { useCallback, useMemo, useState } from 'react';
import { DIYChartOption } from 'constants/chart-selector';
import { isDiyWidgetEngineConfig } from 'utils/normalizeWidgetConfig';

/** Minimal shape the drawer needs from a resolved Enterprise chart type. */
export type EnterpriseChartTypeLike = { id: string; optionValue: string; component: string };

/** Resolves the Enterprise chart type behind a dropdown option value, if any. */
export type EnterpriseChartTypeResolver = (
  optionValue?: string
) => EnterpriseChartTypeLike | undefined;

/** Everything the drawer needs from the chart-selection domain. */
export type UseCurrentAliasResult = {
  /** Options offered by the chart-type dropdown, `undefined` until Guardrails responds. */
  chartTypes: DIYChartOption[] | undefined;
  /** Replaces the dropdown options. */
  setChartTypes: React.Dispatch<React.SetStateAction<DIYChartOption[] | undefined>>;
  /** Chart option currently selected. */
  currentAlias: DIYChartOption | undefined;
  /** Selects a chart option and mirrors the Enterprise flag to the store. */
  setCurrentAlias: (alias: DIYChartOption | undefined) => void;
  /**
   * ENTERPRISE-ONLY: chart type the Enterprise widget renders through, `undefined` for legacy
   * charts. This is the discriminator upcoming chart types branch on.
   */
  enterpriseChartType: EnterpriseChartTypeLike | undefined;
  /** ENTERPRISE-ONLY: derived convenience — `true` when `enterpriseChartType` is set. */
  isDiyWidgetEngine: boolean;
  /**
   * ENTERPRISE-ONLY: `true` when the *persisted config* already is Enterprise V2 (regardless of
   * the current dropdown selection). Used to lock the chart-type dropdown on saved widgets.
   */
  hasEnterpriseV2Config: (config: any) => boolean;
};

/** Inputs of {@link useCurrentAlias}. */
export type UseCurrentAliasParams = {
  /** Config of the widget being edited (second source of the Enterprise discriminator). */
  customWidgetConfig: any;
  /** Global store setter. */
  setState: (partial: any) => void;
  /** Enterprise chart-type registry lookup, injected from the `Enterprise/` seam. */
  resolveEnterpriseChartType: EnterpriseChartTypeResolver;
  /**
   * Chart type assumed for saved Enterprise widgets that predate the discriminator.
   * WHY: legacy configs carry no chart type and were always rendered as a table.
   */
  defaultEnterpriseChartType: EnterpriseChartTypeLike;
  /**
   * Resolves the chart type of a SAVED engine config (envelope / `Chart`). When omitted, saved
   * engine configs fall back to `defaultEnterpriseChartType` (historic behaviour).
   */
  resolveConfigChartType?: (config: any) => EnterpriseChartTypeLike | undefined;
};

export const useCurrentAlias = ({
  customWidgetConfig,
  setState,
  resolveEnterpriseChartType,
  defaultEnterpriseChartType,
  resolveConfigChartType,
}: UseCurrentAliasParams): UseCurrentAliasResult => {
  const [chartTypes, setChartTypes] = useState<DIYChartOption[]>();
  const [currentAlias, setCurrentAliasRaw] = useState<DIYChartOption>();

  /** A saved config is enterprise when its serialized shape says so. */
  const hasEnterpriseV2Config = useCallback(
    (config: any): boolean => isDiyWidgetEngineConfig(config),
    []
  );

  /**
   * A widget is Enterprise either because the user just picked one of the registered Enterprise
   * chart options, or because the config it was loaded from already is an enterprise payload.
   *
   * WHY: both sources are needed — the alias covers the "user is creating one right now" case
   * before anything is persisted, and the config covers reopening a saved enterprise widget,
   * which falls back to the default chart type for backward compatibility.
   */
  const computeEnterpriseChartType = useCallback(
    (alias: DIYChartOption | undefined, config: any): EnterpriseChartTypeLike | undefined => {
      const fromAlias = resolveEnterpriseChartType(alias?.value as string | undefined);
      if (fromAlias) return fromAlias;
      if (!isDiyWidgetEngineConfig(config)) return undefined;
      return resolveConfigChartType ? resolveConfigChartType(config) : defaultEnterpriseChartType;
    },
    [resolveEnterpriseChartType, defaultEnterpriseChartType, resolveConfigChartType]
  );

  const enterpriseChartType = useMemo(
    () => computeEnterpriseChartType(currentAlias, customWidgetConfig),
    [computeEnterpriseChartType, currentAlias, customWidgetConfig]
  );

  const isDiyWidgetEngine = Boolean(enterpriseChartType);

  /**
   * Selects a chart option locally AND publishes the Enterprise flag to the store.
   * WHY: the publication is imperative (not an effect) so widgets rendered outside the drawer see
   * the new mode in the same commit the user changed the chart type.
   */
  const setCurrentAlias = useCallback(
    (alias: DIYChartOption | undefined) => {
      setCurrentAliasRaw(alias);
      setState({
        isDiyWidgetEngine: Boolean(computeEnterpriseChartType(alias, customWidgetConfig)),
      });
    },
    [computeEnterpriseChartType, customWidgetConfig, setState]
  );

  return {
    chartTypes,
    setChartTypes,
    currentAlias,
    setCurrentAlias,
    enterpriseChartType,
    isDiyWidgetEngine,
    hasEnterpriseV2Config,
  };
};
